import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PipelinePayloadRepresentation } from '@common/api';
import { payloadsApi } from '@data/rest/api';
import { retryUnlessRefused } from '@data/rest/retryUnlessRefused';

export const payloadKeys = Object.freeze({
    all: ['payloads'] as const,
    run(sourceFolderId: number, runUuid: string) {
        return [...payloadKeys.all, 'run', sourceFolderId, runUuid] as const;
    },
    downloadUrl(payloadId: string) {
        return [...payloadKeys.all, 'downloadUrl', payloadId] as const;
    },
});

const NO_PAYLOADS: ReadonlyMap<string, PipelinePayloadRepresentation> = new Map();

// Each step's newest payload, from a list that comes newest first
export function newestPayloadPerStep(
    payloads: readonly PipelinePayloadRepresentation[]
): Map<string, PipelinePayloadRepresentation> {
    const newest = new Map<string, PipelinePayloadRepresentation>();
    for (const payload of payloads) if (!newest.has(payload.name)) newest.set(payload.name, payload);
    return newest;
}

/**
 * The payload each extract step stored in one run, by step name. An extract stores its payload before it reports done, so the list is asked for again
 * as each of `doneExtracts` finishes in a run watched while it runs. A viewer the payload API refuses
 * gets none, and the view carries on.
 */
export function useRunPayloads(
    sourceFolderId: number | undefined,
    runUuid: string | undefined,
    doneExtracts: readonly string[]
): ReadonlyMap<string, PipelinePayloadRepresentation> {
    const { data } = useQuery(
        [...payloadKeys.run(sourceFolderId!, runUuid!), ...doneExtracts],
        () => payloadsApi.list(sourceFolderId!, undefined, runUuid!),
        {
            enabled: sourceFolderId !== undefined && runUuid !== undefined && doneExtracts.length > 0,
            staleTime: 30_000,
            retry: retryUnlessRefused,
        }
    );
    return useMemo(() => (data ? newestPayloadPerStep(data) : NO_PAYLOADS), [data]);
}

export function usePayloadDownloadUrl(payloadId: string | null) {
    return useQuery(payloadKeys.downloadUrl(payloadId!), () => payloadsApi.getDownloadUrl(payloadId!), {
        enabled: payloadId !== null,
        // A hidden tab skips the interval, so focus refetches a link that expired meanwhile
        refetchOnWindowFocus: true,
        retry: retryUnlessRefused,
        staleTime: 50 * 60 * 1000, // 50 minutes (signed URLs expire in 1 hour)
        refetchInterval: 50 * 60 * 1000,
    });
}

// Each call signs a fresh URL. A signed URL answers with an attachment disposition, so the browser saves the file.
export function useSignPayloadDownloadUrl(payloadId: string) {
    return useMutation(() => payloadsApi.getDownloadUrl(payloadId));
}
